import React, { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { Edit, Plus, Eye, Building2, ListOrdered, CircleDollarSign, ArchiveX } from "lucide-react";
import { toast } from "sonner";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import * as XLSX from "xlsx";
import { Button } from "../components/ui/button";
import { Icons } from "../components/ui/icons";
import { Input } from "../components/ui/input";
import { Modal } from "../components/ui/modal";
import { CalendarDatePicker } from "../components/ui/calendar-date-picker";
import SearchInput from "../components/search-input";
import DataTable, { TableRowData } from "../components/data-table";
import PageLoadingState from "../components/page-loading-state";
import PageShell from "../components/page-shell";
import { RefreshToolbarButton } from "../components/refresh-toolbar-button";
import { fixedAssetsTableColumns } from "../lib/table-columns";
import {
  FixedAsset,
  FixedAssetAddition,
  FixedAssetDepreciationReport,
  FixedAssetStatisticsResponse,
  AllAssetsDepreciationItem,
  addFixedAssetAddition,
  createFixedAsset,
  getAllAssetsDepreciation,
  getFixedAssetById,
  getFixedAssetDepreciation,
  getFixedAssets,
  getFixedAssetStatistics,
  updateFixedAsset,
  CreateFixedAssetPayload,
} from "../lib/api";
import { getBackendErrorMessage } from "../lib/utils";

const formatMoney = (n: number) =>
  `TZS ${n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

const formatMoneyString = (s: string | null | undefined) => {
  if (s == null || s === "") return "—";
  const n = parseFloat(s);
  if (Number.isNaN(n)) return s;
  return formatMoney(n);
};

const formatRateDecimal = (r: string | null | undefined) => {
  if (r == null || r === "") return "—";
  const v = parseFloat(r);
  if (Number.isNaN(v)) return r;
  return `${(v * 100).toFixed(2)}%`;
};

/** Excel cell text: thousands commas + up to 2 decimals (plain string, not a formula). */
const formatExcelCommaAmount = (v: unknown): string => {
  if (v === "" || v == null) return "";
  const n = typeof v === "number" ? v : parseFloat(String(v));
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
};

/**
 * Integer-only money: commas stripped, fractional part ignored (opening accum., disposal).
 */
const stripMoneyDigits = (s: string) => {
  const noCommas = s.replace(/,/g, "");
  const integerPart = noCommas.split(".")[0] ?? "";
  return integerPart.replace(/\D/g, "");
};

/** Integer string with thousands separators for form fields. */
const formatCommaMoneyInput = (digits: string): string => {
  if (!digits) return "";
  const normalized = digits.replace(/^0+(?=\d)/, "") || "0";
  return normalized.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

/** One decimal separator; strips invalid chars. */
const splitDecimalMoney = (raw: string) => {
  const cleaned = raw.replace(/,/g, "").replace(/[^\d.]/g, "");
  const dotIdx = cleaned.indexOf(".");
  if (dotIdx === -1) {
    return {
      intDigits: cleaned.replace(/\D/g, ""),
      fracDigits: "",
      trailingDot: false as const,
    };
  }
  const intDigits = cleaned.slice(0, dotIdx).replace(/\D/g, "");
  const fracDigits = cleaned.slice(dotIdx + 1).replace(/\D/g, "");
  const trailingDot = cleaned.endsWith(".");
  return { intDigits, fracDigits, trailingDot };
};

/** Raw input → comma grouping on integer part; optional fractional digits (cost, capex amount). */
const formatDecimalMoneyInputFromRaw = (raw: string): string => {
  const { intDigits, fracDigits, trailingDot } = splitDecimalMoney(raw);
  if (!intDigits && !fracDigits && !trailingDot) return "";

  const intGrouped =
    intDigits !== "" ? formatCommaMoneyInput(intDigits) : "0";

  if (trailingDot && fracDigits === "") return `${intGrouped}.`;
  if (fracDigits !== "") return `${intGrouped}.${fracDigits}`;
  return intGrouped;
};

/** Plain numeric string for API (no commas); never ends with a lone "." */
const stringifyDecimalMoneyForApi = (raw: string): string => {
  const { intDigits, fracDigits, trailingDot } = splitDecimalMoney(raw);
  if (!intDigits && !fracDigits && !trailingDot) return "";
  let int = intDigits.replace(/^0+(?=\d)/, "");
  if (int === "" && (fracDigits !== "" || trailingDot)) int = "0";
  if (int === "" && !fracDigits) return "";
  if (fracDigits === "") return int;
  return `${int}.${fracDigits}`;
};

/** Raw input → comma-grouped display (integer fields only). */
const formatMoneyInputFromRaw = (raw: string): string => {
  const digits = stripMoneyDigits(raw);
  if (digits === "") return "";
  return formatCommaMoneyInput(digits);
};

const emptyForm = () => ({
  division: "",
  description: "",
  units: "",
  serialNumber: "",
  depreciationRate: "",
  datePurchased: null as Date | null,
  costPrice: formatCommaMoneyInput("0"),
  openingAccumulatedDepreciation: formatCommaMoneyInput("0"),
  disposedAt: null as Date | null,
  disposalAmount: "",
});

type AssetFormState = ReturnType<typeof emptyForm>;

const toCreatePayload = (f: AssetFormState): CreateFixedAssetPayload => ({
  division: f.division.trim() || null,
  description: f.description.trim(),
  units:
    f.units.trim() === ""
      ? null
      : (() => {
        const u = parseInt(f.units, 10);
        return Number.isNaN(u) ? null : Math.max(0, u);
      })(),
  serialNumber: f.serialNumber.trim() || null,
  depreciationRate: f.depreciationRate.trim() || null,
  datePurchased: f.datePurchased ? format(f.datePurchased, "yyyy-MM-dd") : null,
  costPrice: stringifyDecimalMoneyForApi(f.costPrice) || "0",
  openingAccumulatedDepreciation: stripMoneyDigits(f.openingAccumulatedDepreciation) || "0",
  disposedAt: f.disposedAt ? format(f.disposedAt, "yyyy-MM-dd") : null,
  disposalAmount: (() => {
    const d = stripMoneyDigits(f.disposalAmount);
    return d === "" ? null : d;
  })(),
});

const assetToForm = (a: FixedAsset): AssetFormState => ({
  division: a.division ?? "",
  description: a.description,
  units: a.units != null ? String(a.units) : "",
  serialNumber: a.serialNumber ?? "",
  depreciationRate: a.depreciationRate ?? "",
  datePurchased: a.datePurchased ? new Date(a.datePurchased) : null,
  costPrice: formatDecimalMoneyInputFromRaw(String(a.costPrice ?? "0").replace(/,/g, "")),
  openingAccumulatedDepreciation: formatCommaMoneyInput(
    stripMoneyDigits(a.openingAccumulatedDepreciation ?? "0"),
  ),
  disposedAt: a.disposedAt ? new Date(a.disposedAt) : null,
  disposalAmount: a.disposalAmount
    ? formatCommaMoneyInput(stripMoneyDigits(a.disposalAmount))
    : "",
});

const FixedAssets: React.FC = () => {
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<AssetFormState>(emptyForm);

  const [detailId, setDetailId] = useState<string | null>(null);
  const [depFromYear, setDepFromYear] = useState<number>(
    new Date().getFullYear() - 5,
  );
  const [depToYear, setDepToYear] = useState<number>(new Date().getFullYear());

  const [additionOpen, setAdditionOpen] = useState(false);
  const [additionDate, setAdditionDate] = useState<Date | null>(null);
  const [additionAmount, setAdditionAmount] = useState("");
  const [additionNotes, setAdditionNotes] = useState("");
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [downloadFromYear, setDownloadFromYear] = useState<number | "">("");
  const [downloadToYear, setDownloadToYear] = useState<number | "">("");
  const [downloadDivision, setDownloadDivision] = useState("");
  const [downloadingExcel, setDownloadingExcel] = useState(false);

  const queryClient = useQueryClient();

  const listQuery = useQuery({
    queryKey: ["fixedAssets", { page: currentPage, limit: pageSize, search: searchTerm }],
    queryFn: () => getFixedAssets(currentPage, pageSize, searchTerm),
    placeholderData: keepPreviousData,
  });

  const statisticsQuery = useQuery<FixedAssetStatisticsResponse>({
    queryKey: ["fixedAssets", "statistics"],
    queryFn: () => getFixedAssetStatistics(),
  });

  const detailQuery = useQuery({
    queryKey: ["fixedAssets", detailId],
    queryFn: () => getFixedAssetById(detailId!),
    enabled: !!detailId,
  });

  const depreciationQuery = useQuery({
    queryKey: ["fixedAssets", detailId, "depreciation", depFromYear, depToYear],
    queryFn: () => getFixedAssetDepreciation(detailId!, depFromYear, depToYear),
    enabled: !!detailId,
  });

  const asset = detailQuery.data;

  const makeExcel = ({
    items,
    fromYear,
    toYear,
  }: {
    items: AllAssetsDepreciationItem[];
    fromYear?: number;
    toYear?: number;
  }) => {
    const years = (() => {
      if (fromYear != null && toYear != null) {
        const out: number[] = [];
        for (let y = fromYear; y <= toYear; y++) out.push(y);
        return out;
      }
      const set = new Set<number>();
      for (const it of items) for (const y of it.years ?? []) set.add(y.year);
      return Array.from(set).sort((a, b) => a - b);
    })();

    const header = [
      "Division",
      "Description",
      "Units",
      "Serial number",
      "Date purchased",
      "Rate (p.a.)",
      "Cost price",
      "Opening accumulated depreciation",
      "Disposed at",
      "Disposal amount",
      "Message",
      ...years.flatMap((y) => [
        `${y} Additions`,
        `${y} Depreciation`,
        `${y} Basis end`,
        `${y} Accum. dep end`,
        `${y} NBV end`,
      ]),
    ];

    const toNumber = (v: unknown): number | null => {
      if (v == null || v === "") return null;
      const n = typeof v === "number" ? v : parseFloat(String(v));
      return Number.isFinite(n) ? n : null;
    };

    const aoa = [
      header,
      ...items.map((it) => {
        const a = it.asset;
        const perYear = new Map<number, any>();
        for (const y of it.years ?? []) perYear.set(y.year, y);
        return [
          a.division ?? "",
          a.description ?? "",
          a.units ?? "",
          a.serialNumber ?? "",
          a.datePurchased ?? "",
          it.rate ?? toNumber(a.depreciationRate) ?? "",
          formatExcelCommaAmount(toNumber(a.costPrice) ?? ""),
          formatExcelCommaAmount(
            it.openingAccumulatedDepreciation ?? toNumber(a.openingAccumulatedDepreciation) ?? "",
          ),
          a.disposedAt ?? "",
          formatExcelCommaAmount(toNumber(a.disposalAmount) ?? ""),
          it.message ?? "",
          ...years.flatMap((y) => {
            const row = perYear.get(y);
            return [
              formatExcelCommaAmount(row?.additions ?? ""),
              formatExcelCommaAmount(row?.depreciation ?? ""),
              formatExcelCommaAmount(row?.basisEnd ?? ""),
              formatExcelCommaAmount(row?.accumulatedDepreciationEnd ?? ""),
              formatExcelCommaAmount(row?.nbvEnd ?? ""),
            ];
          }),
        ];
      }),
    ];

    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Depreciation");

    // Basic usability: freeze header row and first columns.
    (ws as any)["!freeze"] = { xSplit: 3, ySplit: 1 };

    return wb;
  };

  const downloadExcel = async () => {
    setDownloadingExcel(true);
    try {
      const fromYear = downloadFromYear === "" ? undefined : downloadFromYear;
      const toYear = downloadToYear === "" ? undefined : downloadToYear;
      const res = await getAllAssetsDepreciation({
        fromYear,
        toYear,
        division: downloadDivision.trim() || undefined,
        search: searchTerm.trim() || undefined,
      });
      const wb = makeExcel({
        items: res.data ?? [],
        fromYear: res.meta.fromYear ?? fromYear,
        toYear: res.meta.toYear ?? toYear,
      });
      const now = new Date();
      const fy = res.meta.fromYear ?? fromYear ?? "";
      const ty = res.meta.toYear ?? toYear ?? "";
      const fileName = `Wapo_Fixed_Assets_Depreciation_${fy || "start"}-${ty || "end"}_${format(now, "yyyyMMdd_HHmm")}.xlsx`;
      XLSX.writeFile(wb, fileName);
      toast.success("Excel downloaded.");
      setDownloadOpen(false);
    } catch (e) {
      toast.error(getBackendErrorMessage(e, "Failed to download report."));
    } finally {
      setDownloadingExcel(false);
    }
  };

  useEffect(() => {
    const handler = setTimeout(() => setSearchTerm(searchInput), 400);
    return () => clearTimeout(handler);
  }, [searchInput]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(() => {
    if (!asset?.datePurchased) return;
    const py = new Date(asset.datePurchased).getFullYear();
    const cy = new Date().getFullYear();
    setDepFromYear(py);
    setDepToYear(Math.max(py, cy));
  }, [asset?.id, asset?.datePurchased]);

  const rows = useMemo(() => {
    const data = listQuery.data?.data ?? [];
    return data.map((a) => ({
      id: a.id,
      description: a.description,
      division: a.division ?? "—",
      serial_number: a.serialNumber ?? "—",
      units: a.units != null ? String(a.units) : "—",
      depreciation_rate: formatRateDecimal(a.depreciationRate),
      cost_price: formatMoneyString(a.costPrice),
      date_purchased: a.datePurchased
        ? format(new Date(a.datePurchased), "dd MMM yyyy")
        : "—",
      raw_data: a,
    })) as TableRowData[];
  }, [listQuery.data]);

  const stats = statisticsQuery.data?.totals ?? null;

  const totalItems = listQuery.data?.meta.total ?? stats?.totalAssets ?? 0;
  const totalPages = listQuery.data?.meta.totalPages ?? 1;
  const initialLoading = listQuery.isLoading || statisticsQuery.isLoading;
  const tableLoading = listQuery.isFetching && !listQuery.isLoading;

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = toCreatePayload(form);
      if (!payload.description.trim()) {
        throw new Error("Description is required.");
      }
      if (editingId) {
        return updateFixedAsset(editingId, payload);
      }
      return createFixedAsset(payload);
    },
    onSuccess: () => {
      toast.success(editingId ? "Asset updated." : "Asset created.");
      queryClient.invalidateQueries({ queryKey: ["fixedAssets"] });
      setFormOpen(false);
      setEditingId(null);
      setForm(emptyForm());
    },
    onError: (e) => toast.error(getBackendErrorMessage(e, "Could not save asset.")),
  });

  const additionMutation = useMutation({
    mutationFn: async () => {
      if (!detailId) throw new Error("No asset selected.");
      if (!additionDate) throw new Error("Choose a date for the addition.");
      const amt = stringifyDecimalMoneyForApi(additionAmount);
      if (!amt || amt === "") throw new Error("Amount is required.");
      return addFixedAssetAddition(detailId, {
        date: format(additionDate, "yyyy-MM-dd"),
        amount: amt,
        notes: additionNotes.trim() || null,
      });
    },
    onSuccess: () => {
      toast.success("Addition recorded.");
      queryClient.invalidateQueries({ queryKey: ["fixedAssets"] });
      setAdditionOpen(false);
      setAdditionDate(null);
      setAdditionAmount("");
      setAdditionNotes("");
    },
    onError: (e) =>
      toast.error(getBackendErrorMessage(e, "Could not add addition.")),
  });

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setFormOpen(true);
  };

  const openEdit = (a: FixedAsset) => {
    setEditingId(a.id);
    setForm(assetToForm(a));
    setFormOpen(true);
  };

  const openDetail = (id: string) => {
    setDetailId(id);
  };

  const closeDetail = () => {
    setDetailId(null);
  };

  const openAddition = () => {
    setAdditionDate(new Date());
    setAdditionAmount("");
    setAdditionNotes("");
    setAdditionOpen(true);
  };

  const actions = [
    {
      label: "View details",
      icon: Eye,
      onClick: (row: TableRowData) => openDetail((row as any).raw_data.id),
    },
    {
      label: "Edit asset",
      icon: Edit,
      onClick: (row: TableRowData) => openEdit((row as any).raw_data as FixedAsset),
    },
  ];

  const depReport: FixedAssetDepreciationReport | undefined = depreciationQuery.data;
  const depMessage =
    depReport?.message ||
    (!depReport?.years?.length && !asset?.datePurchased
      ? "This asset has no purchase date; depreciation cannot be computed."
      : undefined);

  const refresh = () => queryClient.invalidateQueries({ queryKey: ["fixedAssets"] });

  const openDownload = () => {
    const y = new Date().getFullYear();
    if (downloadFromYear === "") setDownloadFromYear(y);
    if (downloadToYear === "") setDownloadToYear(y);
    setDownloadDivision("");
    setDownloadOpen(true);
  };

  return (
    <>
      <PageShell
        title="Fixed assets"
        description="Register, capex additions, and straight-line depreciation reports."
        loading={initialLoading}
        toolbar={
          <>
            <RefreshToolbarButton isRefreshing={listQuery.isFetching} onRefresh={refresh} />
            <Button
              variant="outline"
              className="h-9"
              onClick={openDownload}
              disabled={downloadingExcel}
            >
              <Icons.Download className="h-4 w-4 mr-2" />
              Download Excel
            </Button>
            <Button className="h-9" onClick={openCreate}>
              <Icons.Plus className="h-4 w-4 mr-2" />
              Add asset
            </Button>
          </>
        }
        stats={[
          {
            label: "Total in register",
            value: stats?.totalAssets ?? 0,
            icon: <Building2 className="h-5 w-5 text-primary-600" />,
          },
          {
            label: "Active assets",
            value: stats?.activeAssets ?? 0,
            icon: <ListOrdered className="h-5 w-5 text-primary-600" />,
          },
          {
            label: "Cost basis",
            value: formatMoney(stats?.costBasis ?? 0),
            icon: <CircleDollarSign className="h-5 w-5 text-primary-600" />,
          },
          {
            label: "NBV",
            value: formatMoney(stats?.nbv ?? 0),
            icon: <ArchiveX className="h-5 w-5 text-primary-600" />,
          },
        ]}
        statsColumns={4}
        mobile={{
          onRefresh: refresh,
          primaryAction: { label: "Add asset", icon: Icons.Plus, onClick: openCreate },
          actions: [
            {
              label: "Download Excel",
              icon: Icons.Download,
              onClick: openDownload,
              disabled: downloadingExcel,
            },
          ],
        }}
      >
        <div>
          <div className="flex items-center gap-3 flex-wrap">
            <SearchInput
              searchTerm={searchInput}
              onSearchChange={setSearchInput}
              placeholder="Search description, division, serial…"
            />
          </div>

          <div className="mt-4">
            <DataTable
              columns={fixedAssetsTableColumns}
              data={rows}
              loading={tableLoading}
              actions={actions}
              pagination={{
                page: currentPage,
                pageSize,
                totalItems,
                totalPages,
                onPageChange: setCurrentPage,
                onPageSizeChange: (n) => {
                  setPageSize(n);
                  setCurrentPage(1);
                },
              }}
            />
          </div>
        </div>
      </PageShell>

      <Modal
        isOpen={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditingId(null);
        }}
        title={editingId ? "Edit fixed asset" : "New fixed asset"}
        description="Rates are decimals per annum (e.g. 0.25 = 25%). Amounts are sent as numeric strings."
      >
        <div className="px-6 pb-6 space-y-3 overflow-y-auto max-h-[70vh]">
          <label className="block text-sm font-medium text-gray-700">Description *</label>
          <Input
            value={form.description}
            onChange={(e) => setForm((s) => ({ ...s, description: e.target.value }))}
            placeholder="Asset description"
          />
          <label className="block text-sm font-medium text-gray-700">Division</label>
          <Input
            value={form.division}
            onChange={(e) => setForm((s) => ({ ...s, division: e.target.value }))}
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Units</label>
              <Input
                type="number"
                min={0}
                value={form.units}
                onChange={(e) => setForm((s) => ({ ...s, units: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Serial number</label>
              <Input
                value={form.serialNumber}
                onChange={(e) => setForm((s) => ({ ...s, serialNumber: e.target.value }))}
              />
            </div>
          </div>
          <label className="block text-sm font-medium text-gray-700">
            Depreciation rate (p.a., decimal)
          </label>
          <Input
            value={form.depreciationRate}
            onChange={(e) => setForm((s) => ({ ...s, depreciationRate: e.target.value }))}
            placeholder="0.25"
          />
          <label className="block text-sm font-medium text-gray-700">Date purchased</label>
          <CalendarDatePicker
            selected={form.datePurchased}
            onChange={(d) => setForm((s) => ({ ...s, datePurchased: d }))}
          />
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700">Cost price</label>
              <Input
                inputMode="decimal"
                placeholder="e.g. 45,000,000.50"
                value={form.costPrice}
                onChange={(e) =>
                  setForm((s) => ({
                    ...s,
                    costPrice: formatDecimalMoneyInputFromRaw(e.target.value),
                  }))
                }
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Opening accumulated depreciation
              </label>
              <Input
                inputMode="numeric"
                placeholder="e.g. 0"
                value={form.openingAccumulatedDepreciation}
                onChange={(e) =>
                  setForm((s) => ({
                    ...s,
                    openingAccumulatedDepreciation: formatMoneyInputFromRaw(e.target.value),
                  }))
                }
              />
            </div>
          </div>
          <label className="block text-sm font-medium text-gray-700">Disposed at</label>
          <CalendarDatePicker
            selected={form.disposedAt}
            onChange={(d) => setForm((s) => ({ ...s, disposedAt: d }))}
          />
          <label className="block text-sm font-medium text-gray-700">Disposal amount</label>
          <Input
            inputMode="numeric"
            placeholder="Optional"
            value={form.disposalAmount}
            onChange={(e) =>
              setForm((s) => ({
                ...s,
                disposalAmount: formatMoneyInputFromRaw(e.target.value),
              }))
            }
          />
          <div className="flex justify-end gap-2 pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setFormOpen(false);
                setEditingId(null);
              }}
            >
              Cancel
            </Button>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? "Saving…" : editingId ? "Save changes" : "Create"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={!!detailId}
        onClose={closeDetail}
        title={asset?.description ?? "Asset details"}
        className="max-w-4xl"
        headerAction={
          asset ? (
            <Button
              variant="outline"
              size="sm"
              className="gap-1"
              onClick={() => {
                openEdit(asset);
                closeDetail();
              }}
            >
              <Edit className="h-4 w-4" />
              Edit
            </Button>
          ) : undefined
        }
      >
        <div className="px-6 pb-6 overflow-y-auto max-h-[75vh] space-y-6">
          {detailQuery.isLoading && <PageLoadingState />}
          {detailQuery.isError && (
            <p className="text-sm text-red-600">Could not load asset details.</p>
          )}
          {asset && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                <DetailRow label="Division" value={asset.division ?? "—"} />
                <DetailRow label="Serial" value={asset.serialNumber ?? "—"} />
                <DetailRow label="Units" value={asset.units != null ? String(asset.units) : "—"} />
                <DetailRow label="Depreciation rate" value={formatRateDecimal(asset.depreciationRate)} />
                <DetailRow
                  label="Date purchased"
                  value={
                    asset.datePurchased
                      ? format(new Date(asset.datePurchased), "dd MMM yyyy")
                      : "—"
                  }
                />
                <DetailRow label="Cost price" value={formatMoneyString(asset.costPrice)} />
                <DetailRow
                  label="Opening accum. depreciation"
                  value={formatMoneyString(asset.openingAccumulatedDepreciation)}
                />
                <DetailRow
                  label="Disposed"
                  value={
                    asset.disposedAt
                      ? format(new Date(asset.disposedAt), "dd MMM yyyy")
                      : "—"
                  }
                />
                <DetailRow label="Disposal amount" value={formatMoneyString(asset.disposalAmount)} />
              </div>

              <section>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-sm font-semibold text-gray-900">Capex additions</h3>
                  <Button size="sm" variant="outline" className="gap-1" onClick={openAddition}>
                    <Plus className="h-4 w-4" />
                    Add addition
                  </Button>
                </div>
                <AdditionsTable additions={asset.additions ?? []} />
              </section>

              <section>
                <h3 className="text-sm font-semibold text-gray-900 mb-2">Depreciation report</h3>
                <div className="flex flex-wrap items-end gap-3 mb-3">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">From year</label>
                    <Input
                      type="number"
                      className="w-28"
                      value={depFromYear}
                      onChange={(e) => setDepFromYear(parseInt(e.target.value, 10) || depFromYear)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">To year</label>
                    <Input
                      type="number"
                      className="w-28"
                      value={depToYear}
                      onChange={(e) => setDepToYear(parseInt(e.target.value, 10) || depToYear)}
                    />
                  </div>
                  <RefreshToolbarButton
                    isRefreshing={depreciationQuery.isFetching}
                    onRefresh={() =>
                      queryClient.invalidateQueries({
                        queryKey: ["fixedAssets", detailId, "depreciation"],
                      })
                    }
                  />
                </div>
                {depreciationQuery.isLoading && <p className="text-sm text-gray-500">Loading report…</p>}
                {depreciationQuery.isError && (
                  <p className="text-sm text-red-600">
                    {getBackendErrorMessage(depreciationQuery.error, "Report failed to load.")}
                  </p>
                )}
                {depMessage && (
                  <p className="text-sm text-amber-700 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 mb-2">
                    {depMessage}
                  </p>
                )}
                {depReport && depReport.years.length > 0 && (
                  <DepreciationRegister report={depReport} />
                )}
                {depReport &&
                  depReport.years.length === 0 &&
                  !depMessage &&
                  !depreciationQuery.isLoading && (
                    <p className="text-sm text-gray-500">No rows in this year range.</p>
                  )}
              </section>
            </>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={additionOpen}
        onClose={() => setAdditionOpen(false)}
        title="Record capex addition"
        className="max-w-md"
      >
        <div className="px-6 pb-6 space-y-3">
          <label className="block text-sm font-medium text-gray-700">Date</label>
          <CalendarDatePicker selected={additionDate} onChange={setAdditionDate} />
          <label className="block text-sm font-medium text-gray-700">Amount</label>
          <Input
            inputMode="decimal"
            value={additionAmount}
            onChange={(e) =>
              setAdditionAmount(formatDecimalMoneyInputFromRaw(e.target.value))
            }
            placeholder="e.g. 1,500,000.25"
          />
          <label className="block text-sm font-medium text-gray-700">Notes</label>
          <Input value={additionNotes} onChange={(e) => setAdditionNotes(e.target.value)} />
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setAdditionOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => additionMutation.mutate()}
              disabled={additionMutation.isPending}
            >
              {additionMutation.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={downloadOpen}
        onClose={() => setDownloadOpen(false)}
        title="Download assets depreciation (Excel)"
        description="Uses /assets/depreciation and includes dynamic year columns."
        className="max-w-lg"
      >
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-1">
              <label className="text-sm text-gray-600">From year (optional)</label>
              <Input
                type="number"
                value={downloadFromYear}
                onChange={(e) =>
                  setDownloadFromYear(e.target.value === "" ? "" : parseInt(e.target.value, 10) || "")
                }
              />
            </div>
            <div className="space-y-1">
              <label className="text-sm text-gray-600">To year (optional)</label>
              <Input
                type="number"
                value={downloadToYear}
                onChange={(e) =>
                  setDownloadToYear(e.target.value === "" ? "" : parseInt(e.target.value, 10) || "")
                }
              />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-sm text-gray-600">Division (optional)</label>
            <Input
              value={downloadDivision}
              onChange={(e) => setDownloadDivision(e.target.value)}
              placeholder="Exact match, e.g. MOTOR VEHICLE"
            />
          </div>
          <p className="text-xs text-gray-500">
            Search filter uses the current page search: <span className="font-medium">{searchTerm || "—"}</span>
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setDownloadOpen(false)} disabled={downloadingExcel}>
              Cancel
            </Button>
            <Button onClick={downloadExcel} disabled={downloadingExcel}>
              {downloadingExcel ? "Preparing…" : "Download"}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="font-medium text-gray-900">{value}</p>
    </div>
  );
}

function AdditionsTable({ additions }: { additions: FixedAssetAddition[] }) {
  if (additions.length === 0) {
    return <p className="text-sm text-gray-500">No additions yet.</p>;
  }
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-100">
      <table className="min-w-full text-sm">
        <thead className="bg-gray-50 text-left text-xs text-gray-600">
          <tr>
            <th className="px-3 py-2 font-medium">Date</th>
            <th className="px-3 py-2 font-medium">Amount</th>
            <th className="px-3 py-2 font-medium">Notes</th>
          </tr>
        </thead>
        <tbody>
          {additions.map((a) => (
            <tr key={a.id} className="border-t border-gray-100">
              <td className="px-3 py-2 whitespace-nowrap">
                {format(new Date(a.date), "dd MMM yyyy")}
              </td>
              <td className="px-3 py-2">{formatMoneyString(a.amount)}</td>
              <td className="px-3 py-2 text-gray-700">{a.notes ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DepreciationRegister({ report }: { report: FixedAssetDepreciationReport }) {
  const years = report.years;
  return (
    <div className="space-y-3">
      <p className="text-xs text-gray-500">
        Rate applied: {(report.rate * 100).toFixed(2)}% p.a. · Opening accum. depreciation:{" "}
        {formatMoney(report.openingAccumulatedDepreciation)} · Range {report.fromYear}–{report.toYear}
      </p>
      <div className="overflow-x-auto rounded-lg border border-gray-100">
        <table className="min-w-full text-sm text-right">
          <thead className="bg-gray-50 text-xs text-gray-600">
            <tr>
              <th className="px-3 py-2 text-left font-medium sticky left-0 bg-gray-50 z-[1]">
                Metric
              </th>
              {years.map((y) => (
                <th key={y.year} className="px-3 py-2 font-medium whitespace-nowrap">
                  {y.year}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="tabular-nums">
            <tr className="border-t border-gray-100">
              <td className="px-3 py-2 text-left text-gray-700 sticky left-0 bg-white text-nowrap">Additions</td>
              {years.map((y) => (
                <td key={y.year} className="px-3 py-2 text-nowrap">
                  {formatMoney(y.additions)}
                </td>
              ))}
            </tr>
            <tr className="border-t border-gray-100">
              <td className="px-3 py-2 text-left text-gray-700 sticky left-0 bg-white text-nowrap">
                Depreciation
              </td>
              {years.map((y) => (
                <td key={y.year} className="px-3 py-2 text-nowrap">
                  {formatMoney(y.depreciation)}
                </td>
              ))}
            </tr>
            <tr className="border-t border-gray-100">
              <td className="px-3 py-2 text-left text-gray-700 sticky left-0 bg-white text-nowrap">
                Basis (end)
              </td>
              {years.map((y) => (
                <td key={y.year} className="px-3 py-2 text-nowrap">
                  {formatMoney(y.basisEnd)}
                </td>
              ))}
            </tr>
            <tr className="border-t border-gray-100">
              <td className="px-3 py-2 text-left text-gray-700 sticky left-0 bg-white text-nowrap">
                Accum. depreciation (end)
              </td>
              {years.map((y) => (
                <td key={y.year} className="px-3 py-2 text-nowrap">
                  {formatMoney(y.accumulatedDepreciationEnd)}
                </td>
              ))}
            </tr>
            <tr className="border-t border-gray-100">
              <td className="px-3 py-2 text-left font-medium text-gray-900 sticky left-0 bg-white text-nowrap">
                NBV (end)
              </td>
              {years.map((y) => (
                <td key={y.year} className="px-3 py-2 font-medium text-nowrap">
                  {formatMoney(y.nbvEnd)}
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default FixedAssets;
